import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useOrg } from '../app/OrgContext.js';
import { currencySymbol } from '@goss/types';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Button, Card, Eyebrow, Sparkline, InfoTip } from '../ui/ui.js';
import { METRIC_INFO as M } from '../lib/metricInfo.js';
import type { MetricRow, MetricsResponse, PlacementRow, PlacementsResponse } from '../lib/types.js';

interface Range {
  days?: number;
  since?: string;
  until?: string;
  label: string;
}

/* Live account-level Meta insights (metrics_daily). */

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function abbrev(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function sum(rows: MetricRow[], key: keyof MetricRow): number {
  return rows.reduce((a, r) => a + (Number(r[key]) || 0), 0);
}
function avg(rows: MetricRow[], key: keyof MetricRow): number | null {
  const vals = rows.map((r) => Number(r[key])).filter((v) => Number.isFinite(v));
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

/**
 * Overview — account-level Meta attention metrics. Rendered two ways:
 *   standalone: its own page (legacy /overview route)
 *   embedded:   as the "traffic & attention" section inside Results, where the
 *               booking bottom-line lives — so we drop the greeting + page title.
 */
export function Overview({ embedded = false }: { embedded?: boolean }) {
  const { orgId, me, activeOrg } = useOrg();
  const [rows, setRows] = useState<MetricRow[] | null>(null);
  const [placements, setPlacements] = useState<PlacementRow[] | null>(null);
  const [placementError, setPlacementError] = useState<string | null>(null);
  // Per-ad split: same data at level=ad. Fetched only when asked for — it returns
  // one row per ad x placement, so it's far heavier than the account rollup.
  const [placementLevel, setPlacementLevel] = useState<'account' | 'ad'>('account');
  const [adPlacements, setAdPlacements] = useState<PlacementRow[] | null>(null);
  const [adPlacementError, setAdPlacementError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<Range>({ days: 90, label: 'LAST 90 DAYS' });
  // Greet by name only when there's a real one — never shout an email address.
  const dn = me?.profile?.display_name?.trim() ?? '';
  const name = dn && !dn.includes('@') ? dn : '';

  useEffect(() => {
    if (!orgId) return;
    setRows(null);
    setError(null);
    const p = new URLSearchParams({ level: 'account' });
    if (range.since) p.set('since', range.since);
    if (range.until) p.set('until', range.until);
    if (range.days) p.set('days', String(range.days));
    apiFetch<MetricsResponse>(`/api/orgs/${orgId}/metrics?${p.toString()}`)
      .then((d) => setRows(d.rows))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load metrics'));
  }, [orgId, range]);

  // Placement split is read live from Meta (not metrics_daily), so it loads on its
  // own and a failure here must never take the rest of the page down with it.
  useEffect(() => {
    if (!orgId) return;
    setPlacements(null);
    setPlacementError(null);
    const p = new URLSearchParams();
    if (range.since) p.set('since', range.since);
    if (range.until) p.set('until', range.until);
    if (range.days) p.set('days', String(range.days));
    setAdPlacements(null); // a new range invalidates the per-ad split too
    setAdPlacementError(null);
    apiFetch<PlacementsResponse>(`/api/orgs/${orgId}/placements?${p.toString()}`)
      .then((d) => setPlacements(d.rows))
      .catch((e: unknown) =>
        setPlacementError(e instanceof Error ? e.message : 'Failed to load placements'),
      );
  }, [orgId, range]);

  // Lazy: the per-ad split is only fetched the first time it's actually opened.
  useEffect(() => {
    if (!orgId || placementLevel !== 'ad' || adPlacements || adPlacementError) return;
    const p = new URLSearchParams({ level: 'ad' });
    if (range.since) p.set('since', range.since);
    if (range.until) p.set('until', range.until);
    if (range.days) p.set('days', String(range.days));
    apiFetch<PlacementsResponse>(`/api/orgs/${orgId}/placements?${p.toString()}`)
      .then((d) => setAdPlacements(d.rows))
      .catch((e: unknown) =>
        setAdPlacementError(e instanceof Error ? e.message : 'Failed to load the per-ad split'),
      );
  }, [orgId, range, placementLevel, adPlacements, adPlacementError]);

  const agg = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    // Fall back to THIS workspace's currency, never to baht — a Propty spend
    // figure rendered '฿' is a misreport, not a cosmetic slip.
    const currency = rows.find((r) => r.currency)?.currency ?? activeOrg?.default_currency ?? '';
    const spend = sum(rows, 'spend');
    const impressions = sum(rows, 'impressions');
    const clicks = sum(rows, 'clicks');
    const linkClicks = sum(rows, 'link_clicks');
    const reach = sum(rows, 'reach'); // note: daily reach summed (directional)
    const ctr = impressions ? (clicks / impressions) * 100 : null;
    const cpc = clicks ? spend / clicks : null;
    return {
      currency,
      sym: currencySymbol(currency),
      spend,
      impressions,
      clicks,
      linkClicks,
      reach,
      ctr,
      cpc,
      frequency: avg(rows, 'frequency'),
      hook: avg(rows, 'hook_rate'),
      hold: avg(rows, 'hold_rate'),
      first: sorted[0]?.date,
      last: sorted[sorted.length - 1]?.date,
      spendSeries: sorted.map((r) => Number(r.spend) || 0),
      hookSeries: sorted.map((r) => Number(r.hook_rate) || 0),
      holdSeries: sorted.map((r) => Number(r.hold_rate) || 0),
      freqSeries: sorted.map((r) => Number(r.frequency) || 0),
      dates: sorted.map((r) => r.date),
    };
  }, [rows, activeOrg]);

  return (
    <div
      style={{
        padding: embedded ? '0 0 20px' : '26px 30px 40px',
        animation: embedded ? undefined : 'basilfade .3s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 22,
        }}
      >
        {embedded ? (
          <Eyebrow>META · {range.label}</Eyebrow>
        ) : (
          <div>
            <Eyebrow style={{ marginBottom: 9 }}>
              {greeting()}
              {name ? `, ${name}` : ''} &nbsp;·&nbsp; META · {range.label}
            </Eyebrow>
            <h1 className="page-title">Overview</h1>
          </div>
        )}
        <RangeSelector range={range} onChange={setRange} />
      </div>

      {/* attribution honesty banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '11px 16px',
          background: alpha(C.info, 8),
          border: `1px solid ${alpha(C.info, 30)}`,
          borderRadius: 10,
          marginBottom: 22,
        }}
      >
        <span
          className="mono"
          style={{
            font: `600 9px 'IBM Plex Mono',monospace`,
            color: C.info,
            background: alpha(C.info, 14),
            padding: '3px 7px',
            borderRadius: 4,
            letterSpacing: '.08em',
          }}
        >
          ATTRIB
        </span>
        <div style={{ font: `500 12.5px 'IBM Plex Mono',monospace`, color: C.secondary }}>
          traffic quality —{' '}
          <span style={{ color: C.muted }}>
            attention only (reach, CTR, hook &amp; hold). It diagnoses{' '}
            {embedded ? 'the bookings above' : 'why ads do or don’t book'} — it is not the bottom
            line itself.
          </span>
        </div>
      </div>

      {error && <Card style={{ padding: '16px 20px', color: C.act, fontSize: 13 }}>{error}</Card>}
      {!rows && !error && (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
      )}
      {rows && rows.length === 0 && !error && (
        <Card style={{ padding: '40px', textAlign: 'center' }}>
          <div
            style={{ font: `600 15px 'Outfit',sans-serif`, color: C.secondary, marginBottom: 8 }}
          >
            No metrics yet
          </div>
          <div style={{ fontSize: 13, color: C.muted }}>
            Go to <strong>Connections → Meta Ads → Sync now</strong> to pull performance data.
          </div>
        </Card>
      )}

      {agg && (
        <>
          {/* hero metrics */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 13,
              marginBottom: 13,
            }}
          >
            <Metric label="SPEND" info="spend" value={`${agg.sym}${abbrev(agg.spend)}`} />
            <Metric label="IMPRESSIONS" info="impressions" value={abbrev(agg.impressions)} />
            <Metric
              label="CTR"
              info="ctr"
              value={agg.ctr != null ? agg.ctr.toFixed(2) : '—'}
              unit={agg.ctr != null ? '%' : ''}
              accent
            />
            <Metric
              label="CPC"
              info="cpc"
              value={agg.cpc != null ? `${agg.sym}${agg.cpc.toFixed(2)}` : '—'}
            />
          </div>

          {/* chart + breakdown */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.85fr 1fr',
              gap: 13,
              marginBottom: 13,
            }}
          >
            <Panel title="DAILY SPEND">
              <SpendChart series={agg.spendSeries} dates={agg.dates} sym={agg.sym} />
            </Panel>
            <Panel title="BREAKDOWN">
              <StatRow label="Reach" info="reach" value={abbrev(agg.reach)} note="directional" />
              <StatRow label="Clicks" info="clicks" value={abbrev(agg.clicks)} />
              <StatRow label="Link clicks" info="linkClicks" value={abbrev(agg.linkClicks)} />
              <StatRow
                label="Frequency"
                info="frequency"
                value={agg.frequency != null ? agg.frequency.toFixed(2) : '—'}
              />
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: `1px solid ${C.lineSoft}`,
                  font: `500 11px 'IBM Plex Mono',monospace`,
                  color: C.faint,
                }}
              >
                {agg.first} → {agg.last}
              </div>
            </Panel>
          </div>

          {/* quality */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 13 }}>
            <QualityCard
              label="HOOK RATE"
              info="hook"
              value={agg.hook}
              unit="%"
              color={C.good}
              series={agg.hookSeries}
              help="3-sec views ÷ impressions"
            />
            <QualityCard
              label="HOLD RATE"
              info="hold"
              value={agg.hold}
              unit="%"
              color={C.watch}
              series={agg.holdSeries}
              help="ThruPlay ÷ 3-sec views"
            />
            <QualityCard
              label="FREQUENCY"
              info="frequency"
              value={agg.frequency}
              unit="×"
              color={C.info}
              series={agg.freqSeries}
              help="avg impressions per person"
            />
          </div>

          <PlacementsPanel
            rows={placements}
            error={placementError}
            adRows={adPlacements}
            adError={adPlacementError}
            level={placementLevel}
            onLevel={setPlacementLevel}
            sym={agg.sym}
          />
        </>
      )}
    </div>
  );
}

// Meta returns placement as two raw dimensions; these are the ones that actually
// carry spend for a FB+IG account. Anything unlisted falls back to a tidied
// version of Meta's own key rather than being hidden.
const PLACEMENT_LABELS: Record<string, string> = {
  // Verified against a live account: Meta returns `instagram_stories`,
  // `instagram_reels`, `facebook_stories` — NOT the bare `story` / `reels` the
  // docs suggest. The prefix-stripping fallback below handles those anyway;
  // these entries exist only where Meta's key isn't a readable name.
  'instagram|feed': 'Instagram Feed',
  'instagram|stream': 'Instagram Feed',
  'instagram|instagram_explore_grid_home': 'Instagram Explore',
  'instagram|instagram_explore': 'Instagram Explore',
  'instagram|ig_search': 'Instagram Search',
  'facebook|feed': 'Facebook Feed',
  'facebook|facebook_notification': 'Facebook Notifications',
  'facebook|instream_video': 'Facebook In-Stream Video',
  'facebook|right_hand_column': 'Facebook Right Column',
  'facebook|video_feeds': 'Facebook Video Feed',
};

const tidyWords = (v: string) => v.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function placementLabel(r: PlacementRow): string {
  const mapped = PLACEMENT_LABELS[`${r.platform}|${r.position}`];
  if (mapped) return mapped;
  // Meta prefixes most position keys with the platform (`instagram_stories`),
  // so strip it before joining or the label reads "Instagram Instagram Stories".
  const pos = r.position.startsWith(`${r.platform}_`)
    ? r.position.slice(r.platform.length + 1)
    : r.position;
  return `${tidyWords(r.platform)} ${tidyWords(pos)}`;
}

/**
 * Where the money actually went: Feed vs Stories vs Reels. Basil never restricts
 * ad-set positions, so every campaign already runs across all of them — this is
 * the read-back that says which ones earned their spend.
 */
// A link CTR needs volume before it means anything. Below this, the RATIOS are
// withheld (the raw counts are still facts and still shown) — a 40% CTR off 5
// impressions is noise, and printing it invites someone to act on nothing.
const MIN_IMPRESSIONS_FOR_RATE = 500;

/** Placement rows grouped by the ad that ran them, biggest spender first. */
function groupByAd(rows: PlacementRow[]): {
  adId: string;
  adName: string;
  campaignName: string | null;
  spend: number;
  impressions: number;
  linkClicks: number;
  rows: PlacementRow[];
}[] {
  const byAd = new Map<string, ReturnType<typeof groupByAd>[number]>();
  for (const r of rows) {
    const adId = r.adId ?? 'unknown';
    const g = byAd.get(adId) ?? {
      adId,
      adName: r.adName ?? adId,
      campaignName: r.campaignName,
      spend: 0,
      impressions: 0,
      linkClicks: 0,
      rows: [],
    };
    g.spend += r.spend;
    g.impressions += r.impressions;
    g.linkClicks += r.linkClicks;
    g.rows.push(r);
    byAd.set(adId, g);
  }
  const out = [...byAd.values()];
  for (const g of out) g.rows.sort((a, b) => b.spend - a.spend);
  return out.sort((a, b) => b.spend - a.spend);
}

const PLACEMENT_COLS = 'minmax(150px,1.5fr) 88px 82px 78px 84px';

/**
 * Where the money actually went: Feed vs Stories vs Reels. Basil never restricts
 * ad-set positions, so every campaign already runs across all of them — this is
 * the read-back that says which ones earned their spend.
 *
 * The per-ad view is the one that separates "Stories is weak for us" from "our
 * creative is weak in Stories" — two different problems with different fixes.
 */
function PlacementsPanel({
  rows,
  error,
  adRows,
  adError,
  level,
  onLevel,
  sym,
}: {
  rows: PlacementRow[] | null;
  error: string | null;
  adRows: PlacementRow[] | null;
  adError: string | null;
  level: 'account' | 'ad';
  onLevel: (l: 'account' | 'ad') => void;
  sym: string;
}) {
  const [openAd, setOpenAd] = useState<string | null>(null);
  const perAd = level === 'ad';
  const active = perAd ? adRows : rows;
  const activeError = perAd ? adError : error;
  const spent = (active ?? []).filter((r) => r.spend > 0 || r.impressions > 0);
  const total = spent.reduce((a, r) => a + r.spend, 0);
  const ads = perAd ? groupByAd(spent) : [];

  return (
    <div style={{ marginTop: 13 }}>
      <Panel title="WHERE IT RAN — FEED VS STORIES VS REELS">
        {/* Primary control for this panel — it changes what the whole table means,
            so it reads as a filled segmented button, not a faint text pill. */}
        <div
          style={{
            display: 'inline-flex',
            gap: 3,
            marginBottom: 16,
            padding: 3,
            background: C.surface2,
            border: `1px solid ${C.line}`,
            borderRadius: 9,
          }}
        >
          {(['account', 'ad'] as const).map((l) => {
            const on = level === l;
            return (
              <button
                key={l}
                type="button"
                onClick={() => onLevel(l)}
                style={{
                  padding: '7px 15px',
                  borderRadius: 7,
                  border: `1px solid ${on ? C.basil : 'transparent'}`,
                  font: `700 11px 'IBM Plex Mono',monospace`,
                  letterSpacing: '.08em',
                  cursor: 'pointer',
                  background: on ? C.basil : 'transparent',
                  color: on ? C.basilInk : C.secondary,
                }}
              >
                {l === 'account' ? 'WHOLE ACCOUNT' : 'PER AD'}
              </button>
            );
          })}
        </div>

        {activeError && (
          <div style={{ fontSize: 12.5, color: C.watch, lineHeight: 1.5 }}>
            Couldn&apos;t read the placement split from Meta. {activeError}
          </div>
        )}
        {!activeError && active === null && (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Reading placements from Meta{perAd ? ' for every ad' : ''}…
          </div>
        )}
        {!activeError && active !== null && spent.length === 0 && (
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
            No delivery in this period, so there is nothing to split yet.
          </div>
        )}

        {!activeError && spent.length > 0 && (
          <>
            <PlacementHeader firstLabel={perAd ? 'AD' : 'PLACEMENT'} />

            {!perAd &&
              spent.map((r) => (
                <PlacementLine
                  key={`${r.platform}|${r.position}`}
                  label={placementLabel(r)}
                  share={total ? (r.spend / total) * 100 : 0}
                  row={r}
                  sym={sym}
                />
              ))}

            {perAd &&
              ads.map((g) => {
                const isOpen = openAd === g.adId;
                return (
                  <div key={g.adId}>
                    <div
                      onClick={() => setOpenAd(isOpen ? null : g.adId)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: PLACEMENT_COLS,
                        gap: 8,
                        alignItems: 'center',
                        padding: '10px 0',
                        borderBottom: `1px solid ${C.lineSoft}`,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ minWidth: 0, display: 'flex', gap: 7 }}>
                        <span
                          style={{
                            color: C.faint,
                            transition: 'transform .15s',
                            transform: isOpen ? 'rotate(90deg)' : 'none',
                          }}
                        >
                          ›
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: C.secondary,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {g.adName}
                          </div>
                          <div
                            style={{
                              font: `500 10px 'IBM Plex Mono',monospace`,
                              color: C.faint,
                              marginTop: 2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {g.rows.length} placement{g.rows.length === 1 ? '' : 's'}
                            {g.campaignName ? ` · ${g.campaignName}` : ''}
                          </div>
                        </div>
                      </div>
                      <Cell>
                        {sym}
                        {abbrev(g.spend)}
                      </Cell>
                      <Cell>{abbrev(g.impressions)}</Cell>
                      {g.impressions < MIN_IMPRESSIONS_FOR_RATE ? (
                        <ThinCell title="Too few impressions to read a rate" />
                      ) : (
                        <Cell>{((g.linkClicks / g.impressions) * 100).toFixed(2)}%</Cell>
                      )}
                      {g.impressions < MIN_IMPRESSIONS_FOR_RATE || !g.linkClicks ? (
                        <ThinCell title="Too few impressions to read a rate" />
                      ) : (
                        <Cell>
                          {sym}
                          {(g.spend / g.linkClicks).toFixed(2)}
                        </Cell>
                      )}
                    </div>
                    {isOpen &&
                      g.rows.map((r) => (
                        <PlacementLine
                          key={`${r.adId}|${r.platform}|${r.position}`}
                          label={placementLabel(r)}
                          share={g.spend ? (r.spend / g.spend) * 100 : 0}
                          row={r}
                          sym={sym}
                          indent
                        />
                      ))}
                  </div>
                );
              })}

            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.55, marginTop: 12 }}>
              CTR and cost per click here are LINK clicks, so they compare like for like with the
              Campaigns table. Ads already run in every one of these placements — Basil never
              restricts them. Treat a placement with little spend as unproven rather than bad: Meta
              allocates budget itself, so a low-spend row simply never got a real trial. Rates are
              withheld below {abbrev(MIN_IMPRESSIONS_FOR_RATE)} impressions, where a percentage is
              noise rather than a result.
              {perAd
                ? ' Per ad, the useful question is whether ONE ad works in Stories while others do not — that is a creative problem, not a placement problem.'
                : ''}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function PlacementHeader({ firstLabel }: { firstLabel: string }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: PLACEMENT_COLS,
        gap: 8,
        font: `500 9px 'IBM Plex Mono',monospace`,
        letterSpacing: '.12em',
        color: C.faint,
        paddingBottom: 8,
        borderBottom: `1px solid ${C.lineSoft}`,
      }}
    >
      <div>{firstLabel}</div>
      <div style={{ textAlign: 'right' }}>SPEND</div>
      <div style={{ textAlign: 'right' }}>IMPR</div>
      <div style={{ textAlign: 'right' }}>CTR</div>
      <div style={{ textAlign: 'right' }}>COST / CLICK</div>
    </div>
  );
}

/** One placement row, with a share-of-spend bar and volume-gated rates. */
function PlacementLine({
  label,
  share,
  row,
  sym,
  indent,
}: {
  label: string;
  share: number;
  row: PlacementRow;
  sym: string;
  indent?: boolean;
}) {
  const thin = row.impressions < MIN_IMPRESSIONS_FOR_RATE;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: PLACEMENT_COLS,
        gap: 8,
        alignItems: 'center',
        padding: indent ? '8px 0 8px 22px' : '9px 0',
        borderBottom: `1px solid ${C.lineSoft}`,
        background: indent ? alpha(C.basil, 3) : undefined,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: indent ? 12.5 : 13, color: C.secondary }}>{label}</div>
        {/* Share-of-spend bar: the fastest read of where budget landed. */}
        <div
          style={{
            height: 3,
            borderRadius: 2,
            background: alpha(C.basil, 18),
            marginTop: 5,
            maxWidth: 200,
          }}
        >
          <div
            style={{
              width: `${Math.max(share, 1)}%`,
              height: '100%',
              borderRadius: 2,
              background: C.basil,
            }}
          />
        </div>
      </div>
      <Cell>
        {sym}
        {abbrev(row.spend)}
      </Cell>
      <Cell>{abbrev(row.impressions)}</Cell>
      {thin ? (
        <ThinCell title={`Only ${abbrev(row.impressions)} impressions — too few to read a rate`} />
      ) : (
        <Cell>{row.ctr != null ? `${row.ctr.toFixed(2)}%` : '—'}</Cell>
      )}
      {thin ? (
        <ThinCell title={`Only ${abbrev(row.impressions)} impressions — too few to read a rate`} />
      ) : (
        <Cell>{row.cpc != null ? `${sym}${row.cpc.toFixed(2)}` : '—'}</Cell>
      )}
    </div>
  );
}

/** A rate we refuse to print because the volume behind it is too small. */
function ThinCell({ title }: { title: string }) {
  return (
    <div
      title={title}
      style={{
        textAlign: 'right',
        font: `500 10px 'IBM Plex Mono',monospace`,
        color: C.faint,
      }}
    >
      thin
    </div>
  );
}

function Cell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        textAlign: 'right',
        font: `500 12.5px 'Outfit',sans-serif`,
        fontVariantNumeric: 'tabular-nums',
        color: C.muted,
      }}
    >
      {children}
    </div>
  );
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
// Preset ranges → [since, until] YYYY-MM-DD.
function lastNDays(n: number): [string, string] {
  const until = new Date();
  const since = new Date();
  since.setDate(since.getDate() - n);
  return [iso(since), iso(until)];
}
function monthToDate(): [string, string] {
  const now = new Date();
  return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
}

function RangeSelector({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const isCustom = !!range.since;
  const presets = [7, 30, 90];
  const pill = (active: boolean): React.CSSProperties => ({
    padding: '6px 11px',
    borderRadius: 6,
    font: `500 11.5px 'IBM Plex Mono',monospace`,
    cursor: 'pointer',
    background: active ? C.elevated : 'transparent',
    color: active ? C.primary : C.muted,
  });
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        gap: 3,
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 8,
        padding: 3,
      }}
    >
      {presets.map((d) => (
        <span
          key={d}
          onClick={() => onChange({ days: d, label: `LAST ${d} DAYS` })}
          style={pill(!isCustom && range.days === d)}
        >
          {d}D
        </span>
      ))}
      <span onClick={() => setOpen((v) => !v)} style={pill(isCustom)}>
        CUSTOM
      </span>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              zIndex: 50,
              width: 234,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: 14,
              boxShadow: '0 12px 32px rgba(0,0,0,.4)',
            }}
          >
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {(
                  [
                    { label: 'This month', fn: monthToDate },
                    { label: 'Last 30d', fn: () => lastNDays(30) },
                    { label: 'Last 90d', fn: () => lastNDays(90) },
                  ] as const
                ).map((p) => (
                  <span
                    key={p.label}
                    onClick={() => {
                      const [s, u] = p.fn();
                      setFrom(s);
                      setTo(u);
                    }}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      font: `500 10.5px 'IBM Plex Mono',monospace`,
                      background: C.surface2,
                      border: `1px solid ${C.line}`,
                      color: C.secondary,
                    }}
                  >
                    {p.label}
                  </span>
                ))}
              </div>
              <label
                style={{
                  display: 'grid',
                  gap: 5,
                  font: `500 10px 'IBM Plex Mono',monospace`,
                  color: C.muted,
                  letterSpacing: '.1em',
                }}
              >
                FROM
                <input
                  type="date"
                  className="field"
                  max={today}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label
                style={{
                  display: 'grid',
                  gap: 5,
                  font: `500 10px 'IBM Plex Mono',monospace`,
                  color: C.muted,
                  letterSpacing: '.1em',
                }}
              >
                TO
                <input
                  type="date"
                  className="field"
                  max={today}
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              <Button
                variant="primary"
                disabled={!from || !to}
                onClick={() => {
                  onChange({ since: from, until: to, label: `${from} → ${to}` });
                  setOpen(false);
                }}
              >
                Apply
              </Button>
              <div
                style={{
                  font: `500 9.5px 'IBM Plex Mono',monospace`,
                  color: C.faint,
                  lineHeight: 1.5,
                }}
              >
                Data available for ~the last 90 days.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({
  label,
  info,
  value,
  unit,
  accent,
}: {
  label: string;
  info?: keyof typeof M;
  value: string;
  unit?: string;
  accent?: boolean;
}) {
  return (
    <Card
      style={{
        padding: '17px 19px',
        position: 'relative',
        background: accent ? alpha(C.basil, 8) : C.surface,
        border: `1px solid ${accent ? alpha(C.basil, 40) : C.line}`,
      }}
    >
      {accent && (
        <div
          title="Your north-star metric — the one to watch on this page"
          style={{
            position: 'absolute',
            top: 13,
            right: 13,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: C.basil,
            boxShadow: `0 0 8px ${C.basil}`,
            cursor: 'help',
          }}
        />
      )}
      <div className="label-mono" style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {info && <InfoTip title={M[info].title} text={M[info].text} />}
      </div>
      <div
        style={{
          font: `700 30px/1 'Outfit',sans-serif`,
          color: C.primary,
          marginTop: 14,
          letterSpacing: '-.02em',
        }}
      >
        {value}
        {unit && <span style={{ fontSize: 16, color: accent ? C.basil : C.muted }}>{unit}</span>}
      </div>
    </Card>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card style={{ padding: '18px 20px' }}>
      <div
        style={{
          font: `600 13px 'Outfit',sans-serif`,
          color: C.primary,
          letterSpacing: '.02em',
          marginBottom: 14,
        }}
      >
        {title}
      </div>
      {children}
    </Card>
  );
}

function StatRow({
  label,
  info,
  value,
  note,
}: {
  label: string;
  info?: keyof typeof M;
  value: string;
  note?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        padding: '7px 0',
      }}
    >
      <span style={{ fontSize: 13, color: C.muted, display: 'inline-flex', alignItems: 'center' }}>
        {label}
        {info && <InfoTip title={M[info].title} text={M[info].text} />}
        {note && (
          <span style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
            {' '}
            · {note}
          </span>
        )}
      </span>
      <span style={{ font: `600 15px 'Outfit',sans-serif`, color: C.primary }}>{value}</span>
    </div>
  );
}

function QualityCard({
  label,
  info,
  value,
  unit,
  color,
  series,
  help,
}: {
  label: string;
  info?: keyof typeof M;
  value: number | null;
  unit: string;
  color: string;
  series: number[];
  help: string;
}) {
  const norm = useMemo(() => {
    const max = Math.max(1, ...series);
    return series.map((v) => v / max);
  }, [series]);
  return (
    <Card style={{ padding: '15px 17px' }}>
      <div className="label-mono" title={help} style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {info && <InfoTip title={M[info].title} text={M[info].text} />}
      </div>
      <div
        style={{ font: `700 24px/1 'Outfit',sans-serif`, color: C.primary, margin: '11px 0 9px' }}
      >
        {value != null ? value.toFixed(value < 10 ? 1 : 0) : '—'}
        {value != null && <span style={{ fontSize: 13, color: C.muted }}>{unit}</span>}
      </div>
      {series.some((v) => v > 0) ? (
        <Sparkline points={norm.length > 1 ? norm : [0, 0]} color={color} />
      ) : (
        <div style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
          no video data
        </div>
      )}
    </Card>
  );
}

function SpendChart({ series, dates, sym }: { series: number[]; dates: string[]; sym: string }) {
  const W = 720;
  const H = 190;
  const max = Math.max(1, ...series);
  const n = series.length;
  const pts = series.map((v, i) => {
    const x = n > 1 ? (i / (n - 1)) * W : 0;
    const y = H - (v / max) * (H - 12) - 6;
    return [x, y] as const;
  });
  const line = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  const tick = (i: number) => dates[Math.round((i / 4) * (n - 1))] ?? '';
  return (
    <>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={C.basil} stopOpacity="0.22" />
            <stop offset="1" stopColor={C.basil} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1={H * 0.25} x2={W} y2={H * 0.25} stroke={C.lineSoft} />
        <line x1="0" y1={H * 0.5} x2={W} y2={H * 0.5} stroke={C.lineSoft} />
        <line x1="0" y1={H * 0.75} x2={W} y2={H * 0.75} stroke={C.lineSoft} />
        <path d={area} fill="url(#spendFill)" />
        <path d={line} fill="none" stroke={C.basil} strokeWidth="2" />
      </svg>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          font: `500 9.5px 'IBM Plex Mono',monospace`,
          color: C.faint,
          marginTop: 6,
        }}
      >
        <span>{tick(0)}</span>
        <span>{tick(1)}</span>
        <span>{tick(2)}</span>
        <span>{tick(3)}</span>
        <span>{tick(4)}</span>
      </div>
      <div style={{ marginTop: 8, font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
        peak {sym}
        {abbrev(max)}/day
      </div>
    </>
  );
}
